import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, X, Edit2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SortHeader } from '../../components/SortHeader';
import { useSortableData } from '../../hooks/useSortableData';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Medicine', 'Lab', 'Admin', 'Maintenance', 'Equipment', 'Other'];
const APPROVAL_STATUS = ['Pending', 'Approved', 'Rejected'];
const ORDER_STATUS = ['Pending', 'Ordered', 'Received'];

const APPROVAL_COLORS = {
  'Pending': 'bg-yellow-100 text-yellow-700',
  'Approved': 'bg-green-100 text-green-700',
  'Rejected': 'bg-red-100 text-red-700',
};

const ORDER_COLORS = {
  'Pending': 'bg-gray-100 text-gray-600',
  'Ordered': 'bg-blue-100 text-blue-700',
  'Received': 'bg-green-100 text-green-700',
};

const emptyForm = {
  date: new Date().toISOString().split('T')[0],
  category: '',
  item_details: '',
  requested_by: '',
  ordered_by: '',
  vendor_name: '',
  notes: '',
};

const WantedListModule = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm, requested_by: user.name });
  const [editId, setEditId] = useState(null);
  const [filterApproval, setFilterApproval] = useState('All');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/staff/wanted`, { withCredentials: true });
      setItems(data);
    } catch { toast.error('Failed to load wanted list'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/staff/wanted/${editId}`, form, { withCredentials: true });
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/staff/wanted`, form, { withCredentials: true });
        toast.success('Item added');
      }
      setForm({ ...emptyForm, requested_by: user.name });
      setEditId(null);
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (item) => {
    setForm({
      date: item.date || '',
      category: item.category || '',
      item_details: item.item_details || '',
      requested_by: item.requested_by || '',
      ordered_by: item.ordered_by || '',
      vendor_name: item.vendor_name || '',
      notes: item.notes || '',
    });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleApprovalChange = async (item, newApproval) => {
    try {
      await axios.put(`${API}/staff/wanted/${item.id}`, { management_approval: newApproval }, { withCredentials: true });
      toast.success('Approval updated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Only Admin can change approval');
    }
  };

  const handleOrderStatusChange = async (item, newStatus) => {
    try {
      await axios.put(`${API}/staff/wanted/${item.id}`, { order_status: newStatus }, { withCredentials: true });
      toast.success('Order status updated');
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const handleNotifiedChange = async (item, notified) => {
    try {
      await axios.put(`${API}/staff/wanted/${item.id}`, { notified }, { withCredentials: true });
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const filtered = items.filter(i => filterApproval === 'All' || i.management_approval === filterApproval);
  const { sorted, sortKey, sortDir, requestSort } = useSortableData(filtered, 'date', 'desc');

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Wanted List</h2>
        <div className="flex gap-2">
          <select className="border rounded-lg px-3 py-2 text-sm" value={filterApproval} onChange={e => setFilterApproval(e.target.value)} data-testid="wanted-filter">
            <option value="All">All Status</option>
            {APPROVAL_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button onClick={fetchData} variant="outline" size="icon"><RefreshCw className="h-4 w-4" /></Button>
          <Button onClick={() => { setForm({ ...emptyForm, requested_by: user.name }); setEditId(null); setShowForm(true); }} className="bg-[#0A2540]" data-testid="add-wanted-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Item
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0A2540]">{editId ? 'Edit' : 'New'} Wanted Item</h2>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required data-testid="wanted-date" /></div>
                <div>
                  <Label>Category</Label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} required data-testid="wanted-category">
                    <option value="">Select...</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div><Label>Item Details</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} value={form.item_details} onChange={e => setForm({ ...form, item_details: e.target.value })} required data-testid="wanted-details" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Requested By</Label><Input value={form.requested_by} onChange={e => setForm({ ...form, requested_by: e.target.value })} required data-testid="wanted-requested-by" /></div>
                <div><Label>Ordered By</Label><Input value={form.ordered_by} onChange={e => setForm({ ...form, ordered_by: e.target.value })} data-testid="wanted-ordered-by" /></div>
              </div>
              <div><Label>Vendor Name</Label><Input value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })} data-testid="wanted-vendor" /></div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="wanted-notes" /></div>
              <Button type="submit" className="w-full bg-[#0A2540]" data-testid="wanted-submit">{editId ? 'Update' : 'Add'} Item</Button>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9]">
              <tr>
                <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Category" sortKey="category" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Item Details" sortKey="item_details" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Requested By" sortKey="requested_by" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} className="hidden sm:table-cell" />
                <SortHeader label="Vendor" sortKey="vendor_name" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} className="hidden md:table-cell" />
                <SortHeader label="Approval" sortKey="management_approval" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="center" />
                <SortHeader label="Order" sortKey="order_status" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="center" />
                <th className="px-3 py-3 text-center font-semibold text-[#0A2540] hidden sm:table-cell">Notified</th>
                <th className="px-3 py-3 text-right font-semibold text-[#0A2540]">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[#94A3B8]">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[#94A3B8]">No items found</td></tr>
              ) : sorted.map(item => (
                <tr key={item.id} className="hover:bg-[#F8FAFC]" data-testid={`wanted-row-${item.id}`}>
                  <td className="px-3 py-3 text-[#0A2540]">{item.date}</td>
                  <td className="px-3 py-3"><span className="bg-[#F1F5F9] px-2 py-0.5 rounded text-xs">{item.category}</span></td>
                  <td className="px-3 py-3 max-w-[200px]"><div className="truncate text-[#0A2540]">{item.item_details}</div></td>
                  <td className="px-3 py-3 hidden sm:table-cell text-[#475569]">{item.requested_by}</td>
                  <td className="px-3 py-3 hidden md:table-cell text-[#475569]">{item.vendor_name}</td>
                  <td className="px-3 py-3 text-center">
                    {user.role === 'admin' ? (
                      <select
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${APPROVAL_COLORS[item.management_approval] || ''}`}
                        value={item.management_approval}
                        onChange={e => handleApprovalChange(item, e.target.value)}
                        data-testid={`wanted-approval-${item.id}`}
                      >
                        {APPROVAL_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${APPROVAL_COLORS[item.management_approval] || ''}`}>
                        {item.management_approval}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <select
                      className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${ORDER_COLORS[item.order_status] || ''}`}
                      value={item.order_status}
                      onChange={e => handleOrderStatusChange(item, e.target.value)}
                      data-testid={`wanted-order-${item.id}`}
                    >
                      {ORDER_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-3 text-center hidden sm:table-cell">
                    <button
                      onClick={() => handleNotifiedChange(item, item.notified === 'Yes' ? 'No' : 'Yes')}
                      className={`text-xs font-medium px-2 py-1 rounded-full ${item.notified === 'Yes' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                      data-testid={`wanted-notified-${item.id}`}
                    >
                      {item.notified === 'Yes' ? 'Yes' : 'No'}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => handleEdit(item)} className="p-1.5 rounded-lg hover:bg-[#F1F5F9]" data-testid={`edit-wanted-${item.id}`}>
                      <Edit2 className="h-4 w-4 text-[#475569]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WantedListModule;
